import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { QRCodeCanvas } from 'qrcode.react'
import { QrCode, RefreshCw, Copy, Check, Printer, Download } from 'lucide-react'
import { Button, Card } from '@/components/ui'
import { useRotatePublicKey } from '@/hooks/useParkingZones'
import { encodeFeeParam, feeRowFor } from '@/pages/parking/zones/feeTable'
import type { ParkingZone } from '@/types/parking'

interface PublicCheckInStickerCardProps {
  zone: ParkingZone
}

interface StickerContent {
  zoneName: string
  scanCaption: string
  feeTitle: string
  /** One row per fee-formula line: vehicle label + "฿amount per day/hour". */
  feeRows: { label: string; amount: string }[]
  url: string
}

/**
 * Compose the printable entrance sticker on an offscreen canvas: white
 * background, zone name, the check-in QR (nearest-neighbor upscaled so the
 * modules stay crisp), scan caption, fee table, and the raw URL. Returns null
 * when a 2D context is unavailable (jsdom/tests).
 */
function composeStickerCanvas(
  qrCanvas: HTMLCanvasElement,
  content: StickerContent,
): HTMLCanvasElement | null {
  const W = 800
  const PAD = 48
  const QR_SIZE = 480
  const ROW_H = 46
  const feeBlockH = content.feeRows.length ? 64 + content.feeRows.length * ROW_H : 0

  const canvas = document.createElement('canvas')
  canvas.width = W
  canvas.height = PAD + 56 + 32 + QR_SIZE + 48 + 36 + feeBlockH + 64 + 48 + PAD
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, W, canvas.height)

  // Zone name
  ctx.fillStyle = '#111111'
  ctx.textAlign = 'center'
  ctx.font = 'bold 44px sans-serif'
  let y = PAD + 44
  ctx.fillText(content.zoneName, W / 2, y, W - PAD * 2)
  y += 44

  // QR — crisp nearest-neighbor upscale of the on-page 192px canvas.
  ctx.imageSmoothingEnabled = false
  ctx.drawImage(qrCanvas, (W - QR_SIZE) / 2, y, QR_SIZE, QR_SIZE)
  y += QR_SIZE + 48

  ctx.font = '28px sans-serif'
  ctx.fillStyle = '#333333'
  ctx.fillText(content.scanCaption, W / 2, y)

  // Fee table
  if (content.feeRows.length) {
    y += 64
    ctx.font = 'bold 30px sans-serif'
    ctx.fillStyle = '#111111'
    ctx.fillText(content.feeTitle, W / 2, y)
    ctx.font = '26px sans-serif'
    ctx.fillStyle = '#333333'
    for (const row of content.feeRows) {
      y += ROW_H
      ctx.textAlign = 'left'
      ctx.fillText(row.label, PAD + 32, y, W / 2 - PAD)
      ctx.textAlign = 'right'
      ctx.fillText(row.amount, W - PAD - 32, y, W / 2 - PAD)
    }
    ctx.textAlign = 'center'
  }

  // Raw URL (fallback for phones that can't scan) — naive 2-line wrap.
  y += 64
  ctx.font = '16px monospace'
  ctx.fillStyle = '#666666'
  const mid = Math.ceil(content.url.length / 2)
  if (content.url.length > 60) {
    ctx.fillText(content.url.slice(0, mid), W / 2, y)
    ctx.fillText(content.url.slice(mid), W / 2, y + 24)
  } else {
    ctx.fillText(content.url, W / 2, y)
  }

  return canvas
}

/**
 * Operator-facing "Public self check-in" panel (EP-105), rendered on the zone
 * edit page. Admin-only — the parent gates on `'public_check_in_key' in zone`,
 * which the backend serializes only for admins. Generates/rotates the per-zone
 * apikey and renders the barrier-sticker QR (the `…/parking/:zoneId?apikey=…`
 * link) with copy affordances, plus a composed sticker image (QR + zone fee
 * table from `session_fee_formula`) for download/print.
 */
export function PublicCheckInStickerCard({ zone }: PublicCheckInStickerCardProps) {
  const { t, i18n } = useTranslation('parking')
  const rotate = useRotatePublicKey()
  const qrRef = useRef<HTMLDivElement>(null)
  const [copied, setCopied] = useState(false)

  const url = zone.public_check_in_url ?? null
  const feeLines = zone.session_fee_formula ?? []

  const composeSticker = (): HTMLCanvasElement | null => {
    const qrCanvas = qrRef.current?.querySelector('canvas')
    if (!qrCanvas || !url) return null
    return composeStickerCanvas(qrCanvas, {
      zoneName: zone.name,
      scanCaption: t('zones.public_checkin.scan_to_check_in'),
      feeTitle: t('zones.public_checkin.fee_table_title'),
      feeRows: feeLines.map((line) => feeRowFor(line, t, i18n)),
      url,
    })
  }

  const handleGenerate = () => {
    rotate.mutate(zone.id)
  }

  const handleCopy = async () => {
    if (!url) return
    // Append the encoded fee formula so the public check-in page can render
    // the rate table without needing a separate API round-trip. Skipped when
    // there's no formula.
    const fee = encodeFeeParam(feeLines)
    const linkToCopy = fee ? `${url}&fee=${fee}` : url
    try {
      await navigator.clipboard.writeText(linkToCopy)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard can be unavailable (insecure origin) — silently no-op.
    }
  }

  const handleDownload = () => {
    const sticker = composeSticker()
    if (!sticker) return
    const link = document.createElement('a')
    link.href = sticker.toDataURL('image/png')
    link.download = `parking-sticker-${zone.name.replace(/[^\p{L}\p{N}_-]+/gu, '-')}.png`
    link.click()
  }

  const handlePrint = () => {
    const sticker = composeSticker()
    if (!sticker) return
    const dataUrl = sticker.toDataURL('image/png')
    const win = window.open('', '_blank', 'width=480,height=720')
    if (!win) return
    win.document.write(
      `<html><head><title>${zone.name}</title></head>` +
        `<body style="margin:0;display:flex;align-items:center;justify-content:center;min-height:100vh;">` +
        `<img src="${dataUrl}" style="width:100%;max-width:420px;" />` +
        `</body></html>`,
    )
    win.document.close()
    win.focus()
    win.print()
  }

  return (
    <Card>
      <div className="flex items-start gap-3 mb-4">
        <QrCode className="w-5 h-5 text-primary shrink-0 mt-0.5" />
        <div>
          <h3 className="font-semibold text-base">{t('zones.public_checkin.title')}</h3>
          <p className="text-sm text-base-content/60 mt-1">
            {t('zones.public_checkin.description')}
          </p>
        </div>
      </div>

      {url ? (
        <div className="space-y-4">
          <div ref={qrRef} className="flex justify-center">
            <QRCodeCanvas value={url} size={192} className="w-48 h-48" />
          </div>

          {feeLines.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-base-content/60">
                {t('zones.public_checkin.fee_table_title')}
              </p>
              <ul className="text-sm bg-base-200 rounded px-3 py-2 space-y-1">
                {feeLines.map((line, i) => {
                  const row = feeRowFor(line, t, i18n)
                  return (
                    <li key={i} className="flex justify-between gap-4">
                      <span>{row.label}</span>
                      <span className="font-medium whitespace-nowrap">{row.amount}</span>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}

          <div className="space-y-1">
            <p className="text-xs font-medium text-base-content/60">
              {t('zones.public_checkin.link_label')}
            </p>
            <p className="font-mono text-xs break-all bg-base-200 rounded px-2 py-1.5">{url}</p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="ghost"
              leftIcon={copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              onClick={() => void handleCopy()}
            >
              {copied ? t('zones.public_checkin.copied') : t('zones.public_checkin.copy_link')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              leftIcon={<Download className="w-3 h-3" />}
              onClick={handleDownload}
            >
              {t('zones.public_checkin.download_png')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              leftIcon={<Printer className="w-3 h-3" />}
              onClick={handlePrint}
            >
              {t('zones.public_checkin.print')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              leftIcon={<RefreshCw className="w-3 h-3" />}
              onClick={handleGenerate}
              loading={rotate.isPending}
            >
              {t('zones.public_checkin.rotate')}
            </Button>
          </div>
          <p className="text-xs text-warning">{t('zones.public_checkin.rotate_warning')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-base-content/60">
            {t('zones.public_checkin.not_generated')}
          </p>
          <Button
            size="sm"
            leftIcon={<QrCode className="w-4 h-4" />}
            onClick={handleGenerate}
            loading={rotate.isPending}
          >
            {t('zones.public_checkin.generate')}
          </Button>
        </div>
      )}
    </Card>
  )
}
